import { useEffect, useState } from "react";

type Props = {
  id: string;
  label: string;
  value: string; // ISO yyyy-mm-dd
  onChange: (v: string) => void;
  required?: boolean;
  error?: string;
};

function parseIso(iso: string): { d: string; m: string; y: string } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return { d: "", m: "", y: "" };
  return { y: match[1], m: match[2], d: match[3] };
}

export function DateOfBirth({ id, label, value, onChange, required, error }: Props) {
  const [{ d, m, y }, setParts] = useState(() => parseIso(value));

  useEffect(() => {
    const next = parseIso(value);
    if (next.d !== d || next.m !== m || next.y !== y) {
      setParts(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const update = (next: { d: string; m: string; y: string }) => {
    setParts(next);
    if (next.d.length >= 1 && next.m.length >= 1 && next.y.length === 4) {
      const dd = next.d.padStart(2, "0");
      const mm = next.m.padStart(2, "0");
      onChange(`${next.y}-${mm}-${dd}`);
    } else {
      onChange("");
    }
  };

  const dayId = `${id}-day`;
  const monthId = `${id}-month`;
  const yearId = `${id}-year`;
  const errClass = error ? "input-error" : undefined;

  return (
    <fieldset className="subfield-set">
      <legend className="field-label">
        {label}
        {required && (
          <span className="req" aria-hidden="true">
            *
          </span>
        )}
      </legend>

      <div className="dob-row">
        <div className="subfield">
          <label htmlFor={dayId} className="subfield-label">
            Day
          </label>
          <input
            id={dayId}
            inputMode="numeric"
            placeholder="DD"
            maxLength={2}
            value={d}
            onChange={(e) => update({ d: e.target.value.replace(/\D/g, ""), m, y })}
            className={errClass}
          />
        </div>
        <div className="subfield">
          <label htmlFor={monthId} className="subfield-label">
            Month
          </label>
          <input
            id={monthId}
            inputMode="numeric"
            placeholder="MM"
            maxLength={2}
            value={m}
            onChange={(e) => update({ d, m: e.target.value.replace(/\D/g, ""), y })}
            className={errClass}
          />
        </div>
        <div className="subfield">
          <label htmlFor={yearId} className="subfield-label">
            Year
          </label>
          <input
            id={yearId}
            inputMode="numeric"
            placeholder="YYYY"
            maxLength={4}
            value={y}
            onChange={(e) => update({ d, m, y: e.target.value.replace(/\D/g, "") })}
            className={errClass}
          />
        </div>
      </div>

      {error && (
        <div className="field-error" role="alert">
          {error}
        </div>
      )}
    </fieldset>
  );
}
